import React from "react";
import { Platform } from "../../types/Platforms";
import { cn } from "../../lib/utils";
import { SupplierLogo } from "../ui/SupplierLogo";
import {
  semiBoldText,
  smallTextSize,
  primaryText,
  baseTextSize,
  regularText,
  quaternaryText,
} from "../../styling/stylingHelper";
import { CardWithHeader } from "../ui/CardWithHeader";
import { useTranslation } from "react-i18next";

export interface PriceAlertCardData {
  name: string;
  platform: Platform;
  toDate: string;
  percentChange: number;
}

interface PriceAlertsCardProps {
  data: PriceAlertCardData[];
  title?: string;
  seeAllText?: string;
  onSeeAllClick?: () => void;
}

// Helper to parse date and fallback to a valid Date object
function parseToDate(str: string): Date {
  const d = new Date(str);
  if (isNaN(d.getTime())) return new Date(0);
  return d;
}

export const PriceAlertsCard: React.FC<PriceAlertsCardProps> = ({
  data = [],
  title,
  seeAllText,
  onSeeAllClick,
}) => {
  const { t } = useTranslation();

  // Use translated defaults if not provided
  const displayTitle = title || t('dashboard.recentPriceAlerts');
  const displaySeeAllText = seeAllText || t('common.seeAll');

  // Sorting logic: most recent date, then largest abs percent change
  const sortedAlerts = React.useMemo(() => {
    if (data.length <= 5) return data;
    return [...data].sort((a, b) => {
      const dateA = parseToDate(a.toDate);
      const dateB = parseToDate(b.toDate);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateB.getTime() - dateA.getTime();
      }
      return Math.abs(b.percentChange) - Math.abs(a.percentChange);
    }).slice(0, 5);
  }, [data]);

  return (
    <CardWithHeader
      title={displayTitle}
      seeAllText={displaySeeAllText}
      onSeeAllClick={onSeeAllClick}
      className="max-w-full h-full"
    >
      {sortedAlerts.length === 0 ? (
        <div className="flex items-center justify-center flex-1 min-h-0 w-full">
          <p className={cn(smallTextSize, regularText, quaternaryText, "text-center", "w-full")}>
            {t('dashboard.noPriceAlertsInPeriod')}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2 overflow-y-auto w-full">
          {sortedAlerts.map((alert, index) => {
          const isPositive = alert.percentChange >= 0;
          return (
            <div
              key={index}
              className={cn(
                "flex items-start justify-between gap-3 w-full py-2"
              )}
            >
              {/* Left: Alert info */}
              <div className="flex-1 min-w-0">
                {/* Name */}
                <div
                  className={cn(
                    baseTextSize,
                    semiBoldText,
                    primaryText,
                    "mb-1 truncate"
                  )}
                  title={alert.name}
                >
                  {alert.name}
                </div>
                {/* Platform logo + Date row */}
                <div className="flex items-center gap-2 mt-0.5">
                  <SupplierLogo
                    platform={alert.platform}
                    size="medium"
                    className="object-cover"
                    alt={alert.name}
                  />
                  <span className={cn(smallTextSize, semiBoldText, "text-[#BEC2CC] dark:text-textQuaternary-dark")}>•</span>
                  <div
                    className={cn(
                      smallTextSize,
                      regularText,
                      quaternaryText,
                      "tracking-[-0.14px] whitespace-nowrap"
                    )}
                  >
                    {new Date(alert.toDate).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </div>
                </div>
              </div>
              {/* Right: Percent change */}
              <div
                className={cn(
                  "shrink-0 flex items-center justify-center px-3 py-1 rounded-lg",
                  isPositive ? "bg-[#FEF3F2] dark:bg-red-900/20" : "bg-[#EDFCF5] dark:bg-[#0E8965]/20"
                )}
              >
                <span
                  className={cn(
                    smallTextSize,
                    semiBoldText,
                    "whitespace-nowrap",
                    isPositive
                      ? "text-[#D92D20] dark:text-textRed-dark"
                      : "text-[#069A6E] dark:text-textGreen-dark"
                  )}
                >
                  {(isPositive ? "+" : "") +
                    alert.percentChange.toLocaleString(undefined, {
                      maximumFractionDigits: 1,
                    }) +
                    "%"}
                </span>
              </div>
            </div>
          );
        })}
        </div>
      )}
    </CardWithHeader>
  );
};